import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchPublicWatchlist } from "@/lib/api";
import { Loader2, ArrowUpRight, ArrowDownRight, ExternalLink, AlertTriangle } from "lucide-react";
import HeldByEtfsCard from "@/components/tracker/HeldByEtfsCard";

const INTERVAL_LABEL = { last_7d: "7D", last_30d: "30D", ytd: "YTD" };

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(2)}%`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
};

export default function PublicWatchlist() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const openInTracker = (sym) => {
    navigate(`/?t=${encodeURIComponent(sym)}`);
  };

  // Stocks-only (for the "Held by ETFs" reverse-lookup). Compute once per data change.
  const stockRows = useMemo(
    () => (data?.rows || []).filter((r) => r && r.is_etf === false && !r.error),
    [data]
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchPublicWatchlist(id)
      .then(setData)
      .catch((e) => {
        const msg = e?.response?.status === 404 ? "Watchlist not found or not public." : "Failed to load.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="relative min-h-screen text-zinc-100 grain-overlay">
      <header className="relative z-10 border-b border-zinc-900 bg-[#09090B]/80 backdrop-blur-sm">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="size-7 border border-zinc-700 flex items-center justify-center group-hover:border-zinc-400 transition-colors">
              <span className="font-mono text-[10px] text-zinc-300 font-bold">TR</span>
            </div>
            <div className="font-bold tracking-tight text-sm sm:text-base">TOTAL RETURN TRACKER</div>
          </Link>
          <Link
            to="/"
            data-testid="open-app-link"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-white border border-zinc-800 hover:border-zinc-500 px-2.5 py-1.5 transition-colors"
          >
            Open the App <ExternalLink size={11} />
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
        {loading && (
          <div className="flex items-center justify-center py-20 text-zinc-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading watchlist…
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="public-watchlist-error"
            className="border border-dashed border-zinc-800 p-12 text-center"
          >
            <AlertTriangle className="mx-auto mb-4 text-zinc-700" size={28} />
            <div className="text-label mb-2">Not Available</div>
            <div className="text-sm text-zinc-400">{error}</div>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-6 bg-white text-black px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-zinc-200 transition-colors"
            >
              Go to Tracker <ExternalLink size={11} />
            </Link>
          </div>
        )}

        {!loading && !error && data && (
          <article data-testid="public-watchlist">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-label mb-3">
                <span className="live-dot inline-block" />
                <span>SHARED WATCHLIST · LIVE</span>
              </div>
              <h1
                className="text-3xl sm:text-5xl font-bold tracking-tight"
                data-testid="public-watchlist-name"
              >
                {data.name}
              </h1>
              <p className="mt-3 text-sm text-zinc-400">
                {data.tickers.length} ticker{data.tickers.length === 1 ? "" : "s"} · simple
                total return (price + dividends), data ≥ 2020-01-01.{" "}
                <span className="text-zinc-600 font-mono">{fmtDate(data.generated_at)}</span>
              </p>
            </div>

            {data.rows.length === 0 ? (
              <div className="border border-zinc-800 bg-[#121214] p-10 text-center text-sm text-zinc-500">
                This watchlist has no tickers yet.
              </div>
            ) : (
              <div className="border border-zinc-800 bg-[#121214] overflow-x-auto">
                <table className="w-full text-sm" data-testid="public-digest-table">
                  <thead>
                    <tr className="border-b border-zinc-900 bg-[#0e0e10]">
                      <th className="text-label text-left px-5 py-3 font-normal">Ticker</th>
                      <th className="text-label text-right px-5 py-3 font-normal">Last Price</th>
                      {data.intervals.map((iid) => (
                        <th key={iid} className="text-label text-right px-5 py-3 font-normal">
                          {INTERVAL_LABEL[iid] || iid} TR
                        </th>
                      ))}
                      <th className="px-3 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => {
                      const by = Object.fromEntries((r.intervals || []).map((x) => [x.interval_id, x]));
                      return (
                        <tr key={r.symbol} className="border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/30">
                          <td className="px-5 py-3">
                            <div className="font-mono font-bold">{r.symbol}</div>
                            <div className="text-[11px] text-zinc-500 truncate max-w-[260px]">{r.name}</div>
                          </td>
                          <td className="text-right px-5 py-3 font-mono text-zinc-300">
                            {r.last_price != null ? `$${r.last_price.toFixed(2)}` : "—"}
                          </td>
                          {data.intervals.map((iid) => {
                            const v = by[iid]?.total_return_pct;
                            if (v == null)
                              return (
                                <td key={iid} className="text-right px-5 py-3 font-mono text-zinc-600">
                                  —
                                </td>
                              );
                            const up = v >= 0;
                            return (
                              <td
                                key={iid}
                                className={`text-right px-5 py-3 font-mono ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}
                              >
                                <span className="inline-flex items-center gap-1 justify-end">
                                  {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                                  {fmtPct(v)}
                                </span>
                              </td>
                            );
                          })}
                          <td className="px-3 py-3">
                            <Link
                              to="/"
                              className="text-zinc-500 hover:text-white p-1 inline-block"
                              title="Open in tracker"
                            >
                              <ArrowUpRight size={14} />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Reverse-holdings: show "Held by ETFs" for each stock (non-ETF) in the watchlist.
                Drives the sticky-traffic loop — visitors clicking through to discover ETFs. */}
            {stockRows.length > 0 && (
              <section
                data-testid="public-held-by-etfs-section"
                className="mt-8 space-y-4"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h2 className="text-label text-zinc-300">
                    Held by ETFs &middot; reverse lookup
                  </h2>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                    discover which ETFs own these stocks
                  </span>
                </div>
                {stockRows.map((r) => (
                  <HeldByEtfsCard
                    key={`held-${r.symbol}`}
                    symbol={r.symbol}
                    isEtf={false}
                    onPickTicker={openInTracker}
                  />
                ))}
              </section>
            )}

            <div className="mt-8 border border-dashed border-zinc-800 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <div className="text-label mb-1">Want your own?</div>
                <div className="text-sm text-zinc-300">
                  Track any US stock or ETF, see reinvested + simple total return, save your own watchlist.
                </div>
              </div>
              <Link
                to="/"
                data-testid="cta-open-app"
                className="inline-flex items-center gap-1.5 bg-white text-black px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-zinc-200 transition-colors whitespace-nowrap"
              >
                Open the App <ExternalLink size={11} />
              </Link>
            </div>
          </article>
        )}
      </main>

      <footer className="relative z-10 border-t border-zinc-900 mt-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-zinc-500 font-mono uppercase tracking-widest">
          <div>Quote: Finnhub · History &amp; dividends: Yahoo Finance · Data ≥ 2020-01-01</div>
          <div>
            Powered by{" "}
            <a
              data-testid="dividend-community-link"
              href="https://www.dividend.community"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-300 hover:text-white underline-offset-4 hover:underline transition-colors normal-case tracking-normal"
            >
              Dividend Community
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
